import React, { useEffect, useState } from 'react';
import api from '../api/axios';
import {
  Box,
  Paper,
  Typography,
  Button,
  Divider,
  Stack,
  Card,
  CardContent,
  CircularProgress,
  TextField,
  IconButton,
  Avatar,
} from '@mui/material';
import { useNavigate, useParams } from 'react-router-dom';
import {
  ArrowBack as ArrowBackIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Announcement as AnnouncementIcon,
  Send as SendIcon,
  Close as CloseIcon,
} from '@mui/icons-material';
import { useDarkMode } from '../App';
import { useDialog } from '../contexts/DialogProvider';
import { sanitizeNoticeHtml } from '../utils/sanitizeHtml';

function NoticeDetailPage({ user }) {
  const { darkMode } = useDarkMode();
  const { confirm, toast } = useDialog();
  const [notice, setNotice] = useState(null);
  const [comments, setComments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [commentLoading, setCommentLoading] = useState(false);
  const [commentContent, setCommentContent] = useState('');
  const [deletingCommentId, setDeletingCommentId] = useState(null);
  const navigate = useNavigate();
  const { id } = useParams();

  useEffect(() => {
    fetchNotice();
    fetchComments();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const fetchNotice = async () => {
    try {
      const res = await api.get(`/notices/${id}`);
      // console.log('raw notice.content:', res.data.content);
      setNotice(res.data);
    } catch (err) {
      console.error('공지 조회 실패:', err);
      setNotice(null);
    } finally {
      setLoading(false);
    }
  };

  const fetchComments = async () => {
    try {
      const res = await api.get(`/comments/${id}`);
      setComments(res.data);
    } catch (err) {
      console.error('댓글 조회 실패:', err);
      setComments([]);
    }
  };

  const handleSubmitComment = async (e) => {
    e.preventDefault();

    if (!commentContent.trim()) {
      toast('댓글 내용을 입력해주세요.', 'warning');
      return;
    }

    try {
      setCommentLoading(true);
      const res = await api.post(`/comments/${id}`, { content: commentContent });
      setComments([...comments, res.data.comment]);
      setCommentContent('');
    } catch (err) {
      console.error('댓글 작성 실패:', err);
      toast(err?.response?.data?.message || '댓글 작성 실패', 'error');
    } finally {
      setCommentLoading(false);
    }
  };

  const handleDeleteComment = async (commentId) => {
    if (!(await confirm({ title: '댓글 삭제', message: '댓글을 삭제하시겠습니까?', confirmText: '삭제', color: 'error' }))) return;

    try {
      setDeletingCommentId(commentId);
      await api.delete(`/comments/${commentId}`);
      setComments(comments.filter(c => c.id !== commentId));
    } catch (err) {
      console.error('댓글 삭제 실패:', err);
      toast(err?.response?.data?.message || '댓글 삭제 실패', 'error');
    } finally {
      setDeletingCommentId(null);
    }
  };

  const handleDeleteNotice = async () => {
    if (!(await confirm({ title: '공지 삭제', message: '정말 이 공지를 삭제하시겠습니까?', confirmText: '삭제', color: 'error' }))) return;

    try {
      await api.delete(`/notices/${id}`);
      toast('공지 삭제 완료');
      navigate('/notice');
    } catch (err) {
      toast('공지 삭제 실패', 'error');
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '60vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!notice) {
    return (
      <Box p={3} textAlign="center">
        <Typography variant="body1">공지 정보를 불러올 수 없습니다.</Typography>
        <Button startIcon={<ArrowBackIcon />} sx={{ mt: 2 }} onClick={() => navigate('/notice')}>
          목록으로 돌아가기
        </Button>
      </Box>
    );
  }

  return (
    <Box sx={{ pt: { xs: 2, sm: 3 }, pb: 2, px: 2, maxWidth: 700, mx: 'auto' }}>
      {/* 헤더 카드 */}
      <Card
        sx={{
          mb: 3,
          borderRadius: 3,
          boxShadow: 3,
          transition: '0.2s',
          '&:hover': { boxShadow: 5 },
        }}
      >
        <CardContent>
          <Stack direction="row" alignItems="center" spacing={1} mb={1}>
            <AnnouncementIcon sx={{ color: 'primary.main' }} />
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              {notice.title}
            </Typography>
          </Stack>
          <Typography variant="body2" color="text.secondary">
            {notice.creator?.name || '관리자'} |{' '}
            {notice.createdAt && new Date(notice.createdAt).toLocaleString('ko-KR')}
          </Typography>
        </CardContent>
      </Card>

      {/* 본문 - HTML 렌더링 */}
      <Paper
        sx={{
          p: 3,
          borderRadius: 3,
          boxShadow: 2,
          minHeight: 200,
          backgroundColor: 'background.paper',
          mb: 3,
          // ✅ 제목 스타일
          '& h1, & h2, & h3, & h4, & h5, & h6': {
            marginTop: '24px',
            marginBottom: '16px',
            fontWeight: 700,
            lineHeight: 1.2,
          },
          '& h1': { fontSize: '2rem' },
          '& h2': { fontSize: '1.75rem' },
          '& h3': { fontSize: '1.5rem' },
          '& h4': { fontSize: '1.25rem' },
          '& h5': { fontSize: '1.1rem' },
          '& h6': { fontSize: '1rem' },
          // ✅ 문단 스타일 (가장 중요!)
          '& p': {
            margin: '0 0 1.2em 0',
            lineHeight: 1.8,
            minHeight: '1.2em',
            color: darkMode ? '#e0e0e0' : '#333',
            whiteSpace: 'normal', // ✅ 공백과 줄바꿈 유지
            wordBreak: 'break-word', // ✅ 긴 단어 자동 줄바꿈
          },
          // ✅ 마지막 p 태그는 margin 제거
          '& p:last-of-type': {
            marginBottom: 0,
          },
          // ✅ 이미지 스타일
          '& img, & .tiptap-image': {
            maxWidth: '100%',
            height: 'auto',
            borderRadius: '8px',
            margin: '20px 0', // ✅ 이미지 위아래 간격
            display: 'block',
          },
          // ✅ 영상 스타일
          '& iframe': {
            maxWidth: '100%',
            borderRadius: '8px',
            margin: '20px 0',
            display: 'block',
          },
          // ✅ 리스트 스타일
          '& ul, & ol': {
            marginLeft: '24px',
            marginBottom: '16px', // ✅ 간격 증가
            marginTop: '8px',
          },
          '& li': {
            marginBottom: '12px', // ✅ 각 항목 간격
            lineHeight: 1.8,
          },
          // ✅ 인용문 스타일
          '& blockquote': {
            borderLeft: '4px solid #1976d2',
            marginLeft: '0',
            marginRight: '0',
            marginTop: '16px',
            marginBottom: '16px',
            paddingLeft: '16px',
            paddingTop: '8px',
            paddingBottom: '8px',
            backgroundColor: darkMode ? 'rgba(25, 118, 210, 0.15)' : '#f0f7ff',
            borderRadius: '4px',
            fontStyle: 'italic',
            opacity: 0.9,
          },
          // ✅ 코드 스타일
          '& code': {
            backgroundColor: darkMode ? 'rgba(255, 255, 255, 0.1)' : '#f5f5f5',
            padding: '2px 6px',
            borderRadius: '3px',
            fontFamily: '"Monaco", "Courier New", monospace',
            fontSize: '0.9em',
            color: darkMode ? '#f48fb1' : '#d63384',
          },
          // ✅ 코드 블록 스타일
          '& pre': {
            backgroundColor: darkMode ? 'rgba(255, 255, 255, 0.05)' : '#f5f5f5',
            padding: '16px',
            borderRadius: '8px',
            overflowX: 'auto',
            marginTop: '16px',
            marginBottom: '16px',
            lineHeight: 1.5,
            border: darkMode ? '1px solid rgba(255, 255, 255, 0.12)' : '1px solid #e0e0e0',
            '& code': {
              backgroundColor: 'transparent',
              padding: 0,
              color: 'inherit',
            },
          },
          // ✅ 링크 스타일
          '& a': {
            color: darkMode ? '#90caf9' : '#1976d2',
            textDecoration: 'underline',
            fontWeight: 500,
            '&:hover': {
              color: darkMode ? '#64b5f6' : '#1565c0',
              textDecoration: 'underline',
            },
          },
          // ✅ 수평선 스타일
          '& hr': {
            margin: '24px 0',
            borderTop: darkMode ? '1px solid rgba(255, 255, 255, 0.12)' : '1px solid #e0e0e0',
            borderBottom: 'none',
          },
          // ✅ 강조 태그
          '& strong, & b': {
            fontWeight: 700,
          },
          '& em, & i': {
            fontStyle: 'italic',
          },
          // ✅ 테이블 스타일
          '& table': {
            width: '100%',
            borderCollapse: 'collapse',
            marginTop: '16px',
            marginBottom: '16px',
            border: darkMode ? '1px solid rgba(255, 255, 255, 0.12)' : '1px solid #ddd',
          },
          '& th, & td': {
            border: darkMode ? '1px solid rgba(255, 255, 255, 0.12)' : '1px solid #ddd',
            padding: '12px',
            textAlign: 'left',
          },
          '& th': {
            backgroundColor: darkMode ? 'rgba(255, 255, 255, 0.05)' : '#f5f5f5',
            fontWeight: 600,
          },
          '& tr:nth-child(even)': {
            backgroundColor: darkMode ? 'rgba(255, 255, 255, 0.02)' : '#fafafa',
          },
          // ✅ 빈 p 태그 제거 (줄바꿈만 있는 경우)
          // '& p:empty': {
          //   display: 'none',
          // },
        }}
      >
        {/* 저장된 HTML 을 sanitize 한 뒤 렌더링 (스크립트·이벤트 핸들러·javascript: URL 제거) */}
        <Box dangerouslySetInnerHTML={{ __html: sanitizeNoticeHtml(notice.content) }} />
      </Paper>

      {/* 하단 버튼 */}
      <Stack direction="row" justifyContent="flex-end" spacing={1} mb={4}>
        <Button variant="outlined" startIcon={<ArrowBackIcon />} onClick={() => navigate('/notice')}>
          목록으로
        </Button>

        {user?.role === 'ADMIN' && (
          <>
            <Button
              variant="contained"
              color="primary"
              startIcon={<EditIcon />}
              onClick={() => navigate(`/notice/${id}/edit`)}
            >
              수정
            </Button>
            <Button
              variant="contained"
              color="error"
              startIcon={<DeleteIcon />}
              onClick={handleDeleteNotice}
            >
              삭제
            </Button>
          </>
        )}
      </Stack>

      <Divider sx={{ my: 3 }} />

      {/* 댓글 섹션 */}
      <Box sx={{ mt: 4 }}>
        <Typography variant="h6" sx={{ fontWeight: 600, mb: 3 }}>
          댓글 ({comments.length})
        </Typography>

        {/* 댓글 작성 폼 */}
        <Paper
          component="form"
          onSubmit={handleSubmitComment}
          sx={{
            p: 2.5,
            mb: 3,
            borderRadius: 2,
            backgroundColor: darkMode ? 'grey.800' : '#f9f9f9',
            border: darkMode ? '1px solid rgba(255, 255, 255, 0.12)' : '1px solid #e0e0e0',
          }}
        >
          <TextField
            fullWidth
            multiline
            rows={3}
            placeholder="댓글을 입력하세요..."
            value={commentContent}
            onChange={(e) => setCommentContent(e.target.value)}
            variant="outlined"
            disabled={commentLoading}
            sx={{ mb: 1.5 }}
          />
          <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
            <Button
              type="submit"
              variant="contained"
              startIcon={<SendIcon />}
              disabled={commentLoading || !commentContent.trim()}
            >
              {commentLoading ? '작성 중...' : '댓글 작성'}
            </Button>
          </Box>
        </Paper>

        {/* 댓글 목록 */}
        {comments.length === 0 ? (
          <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 3 }}>
            댓글이 없습니다. 첫 댓글을 작성해보세요!
          </Typography>
        ) : (
          <Stack spacing={2}>
            {comments.map((comment) => (
              <Card
                key={comment.id}
                sx={{
                  borderRadius: 2,
                  boxShadow: 1,
                  transition: 'all 0.2s',
                  '&:hover': { boxShadow: 3 },
                }}
              >
                <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
                  {/* 댓글 헤더 */}
                  <Stack direction="row" justifyContent="space-between" alignItems="flex-start" mb={1.5}>
                    <Stack direction="row" spacing={1.5} alignItems="center">
                      <Avatar
                        sx={{
                          width: 32,
                          height: 32,
                          fontSize: '0.9rem',
                          backgroundColor: '#1976d2',
                        }}
                      >
                        {comment.user?.name?.charAt(0) || '?'}
                      </Avatar>
                      <Box>
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                          {comment.user?.name || '익명'}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {new Date(comment.createdAt).toLocaleString('ko-KR')}
                        </Typography>
                      </Box>
                    </Stack>

                    {/* 삭제 버튼 */}
                    {(user?.id === comment.user_id || user?.role === 'ADMIN') && (
                      <IconButton
                        size="small"
                        onClick={() => handleDeleteComment(comment.id)}
                        disabled={deletingCommentId === comment.id}
                        sx={{
                          color: '#f44336',
                          '&:hover': { backgroundColor: darkMode ? 'rgba(244, 67, 54, 0.15)' : '#ffebee' },
                        }}
                      >
                        <CloseIcon fontSize="small" />
                      </IconButton>
                    )}
                  </Stack>

                  {/* 댓글 내용 */}
                  <Typography
                    variant="body2"
                    sx={{
                      whiteSpace: 'pre-wrap',
                      wordBreak: 'break-word',
                      lineHeight: 1.6,
                      color: darkMode ? '#e0e0e0' : '#333',
                    }}
                  >
                    {comment.content}
                  </Typography>
                </CardContent>
              </Card>
            ))}
          </Stack>
        )}
      </Box>
    </Box>
  );
}

export default NoticeDetailPage;